import type { ITransferCharge, ITransferTotals } from './types';

import { useCallback, useEffect, useState } from 'react';

import { API_URL } from 'constant';
import { useNetwork } from 'hooks';

/**
 * THE OWNERSHIP-TRANSFER CHARGE RECORDS, period by period.
 *
 * `GET /evault-transfer-charges?period=current|YYYY-MM` — the server's own
 * record of what it billed. One row per committed transfer, newest first, plus
 * the `totals` it summed and the `periods` it has any charge in. Nothing here
 * re-adds a price: the figures on screen are the figures on the invoice.
 *
 * Not on `useEvaultApi` like the rest of this deck. That transport carries the
 * scoped vault token to the eVault service; this endpoint is clients.api's, on
 * the gateway, and is read with the panel's ordinary `useNetwork` — the same
 * way `use-billing-deck` read it while the list lived on the billing page.
 */
export const useTransferCharges = () => {
	const { get } = useNetwork({ updateState: false });

	const [period, setPeriod] = useState<string>('current');
	const [rows, setRows] = useState<ITransferCharge[]>([]);
	const [totals, setTotals] = useState<ITransferTotals>({
		quantity: 0,
		amount: 0,
	});
	const [periods, setPeriods] = useState<string[]>([]);
	const [isLoaded, setIsLoaded] = useState(false);

	const load = useCallback(async (next: string) => {
		/* Re-armed at the START of every fetch, not just the first one — a period
		   change has to go back through "not loaded yet" too, or switching from a
		   month with rows to one without shows the OLD rows for a beat and then
		   nothing, rather than the honest gap in between. */
		setIsLoaded(false);
		const resp: any = await get(
			`${API_URL.EVAULT_TRANSFER_CHARGES}?period=${encodeURIComponent(next)}`
		).catch(() => null);
		const d = resp?.data ?? resp;
		setRows(Array.isArray(d?.rows) ? d.rows : []);
		setTotals(d?.totals ?? { quantity: 0, amount: 0 });
		setPeriods(Array.isArray(d?.periods) ? d.periods : []);
		/* Set LAST, and on the failure path too. A request that never answers
		   leaves an empty list and an honest "no transfers" — never a spinner
		   with nothing behind it. */
		setIsLoaded(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		load(period);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [period]);

	return { rows, totals, periods, period, setPeriod, isLoaded };
};
